// lib/koi-pond-v3/postprocess/moonLensFlare.ts
//
// T9.4 — anamorphic moon lens flare. A single horizontal streak through
// the moon (the cool blue-white smear an anamorphic lens throws off a
// bright point), tightly restrained — cinematic weight without drawing
// attention.
//
// Implemented as an additive billboard quad pinned to the moon. It lives
// on the moon's layer (1) so it composites SHARP over the DoF'd frame
// alongside the disc, and it's screen-aligned each frame (quaternion =
// camera) so the streak stays horizontal regardless of orbit.

import {
  PlaneGeometry,
  ShaderMaterial,
  Mesh,
  Color,
  Vector3,
  AdditiveBlending,
  type Camera,
} from 'three';
import { palette } from '../palette';

export function makeMoonLensFlare(moonPos: Vector3) {
  const geo = new PlaneGeometry(16, 3.2);
  const mat = new ShaderMaterial({
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    depthTest: false, // a flare sits over everything
    uniforms: {
      uColor: { value: new Color(palette.rimLight.hex) }, // cool blue-white
      uIntensity: { value: 0.26 }, // barely-there — cinematic, not attention-grabbing
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform vec3 uColor;
      uniform float uIntensity;
      varying vec2 vUv;
      void main() {
        vec2 d = vUv - 0.5;
        // razor-thin in Y, long falloff in X → the horizontal anamorphic streak
        float streak = exp(-d.y * d.y * 240.0) * exp(-d.x * d.x * 8.0);
        // a small soft core glow right at the moon
        float core = exp(-(d.x * d.x + d.y * d.y) * 55.0);
        float a = (streak * 0.9 + core * 0.5) * uIntensity;
        gl_FragColor = vec4(uColor, a); // additive (src.rgb * src.a + dst)
      }
    `,
  });
  const mesh = new Mesh(geo, mat);
  mesh.position.copy(moonPos);
  mesh.renderOrder = 5;

  const baseIntensity = mat.uniforms.uIntensity.value as number;

  // screen-align the quad so the streak is always horizontal on screen
  function update(camera: Camera) {
    mesh.quaternion.copy(camera.quaternion);
  }

  // T10.3 — dim the flare with the moon (cloud-dimming)
  function setDim(v: number) {
    mat.uniforms.uIntensity.value = baseIntensity * v;
  }

  function dispose() {
    geo.dispose();
    mat.dispose();
  }

  return { mesh, update, setDim, dispose };
}
